/* eslint-disable react-refresh/only-export-components */

import { usePollingSubscription } from "@/hooks/usePollingSubscription";
import { useAppSelector } from "@/redux/dispatch";
import { RootState } from "@/redux/store/rootReducers";
import { incidentsApi } from "@/services/api/incidents";
import { Coordinates, Incident, MapIncident } from "@/types/incidents";
import { getIncidentLocation } from "@/utils/locationUtils";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { useSelectedAlert } from "./SelectedAlertContext";

interface IncidentsContextType {
  incidents: Incident[];
  incidentCount: number;
  mapIncidents: MapIncident[];
  isLoading: boolean;
  error: string | null;
  fetchIncidents: () => Promise<void>;
  refreshIncidents: () => Promise<void>;
  subscriptionStatus: "connecting" | "connected" | "error" | null;
  getLocationAddressOfCurrentLatLng: (coords: Coordinates, incident: Incident) => Promise<void>;
  markAsReadMessage: (selectedAlertID: number, groupId: string) => void;
}

const IncidentsContext = createContext<IncidentsContextType | undefined>(
  undefined
);

export function IncidentsProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { isAuthenticated } = useAuth();
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const { setSelectedAlertId } = useSelectedAlert();

  const [mapIncidents, setMapIncidents] = useState<MapIncident[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<
    "connecting" | "connected" | "error" | null
  >(null);
  const [incidentCount, setIncidentCount] = useState(0);
  const { baseLocation } = useAppSelector((state: RootState) => state.location);
  const processIncidents = useCallback((incidentsData: any[]) => {
    return incidentsData.map((incident) => ({
      ...incident,
      coordinates: getIncidentLocation(incident),
    }));
  }, []);

  const getLocationAddressOfCurrentLatLng = useCallback(async (coords: Coordinates, incident: any): Promise<void> => {
    try {
      const { lat, lng } = coords;
      const response: any = await incidentsApi.getLocationAddress({ lat, lng });
      setMapIncidents((prevIncidents: any) => {
        const tempData = structuredClone(prevIncidents);
        for (const a of tempData) {
          if (a.incidentID === incident.incidentID) {
            a.locationAddressData = response.data;
            a.lastLocationAddressTime = new Date().toISOString();
          }
        }
        return tempData;
      })
      setIncidents((prevIncidents: any) => {
        const tempData = structuredClone(prevIncidents);
        for (const a of tempData) {
          if (a.incidentID === incident.incidentID) {
            a.locationAddressData = response.data;
            a.lastLocationAddressTime = new Date().toISOString();
          }
        }
        return tempData;
      })
    } catch (error) {
      console.error('Error fetching location address:', error);
    }
  }, [])

  const markAsReadMessage = useCallback((selectedAlertID: number, groupId: string) => {
    if (groupId) {
      setIncidents((prevIncidents) => prevIncidents.map((inc: any) => {
        if (inc.groupID === groupId)
          return {
            ...inc,
            isUnreadMessage: false,
          }
        else
          return inc
      }
      ))
    }
    else {
      setIncidents((prevIncidents) => prevIncidents.map((inc: any) =>
        inc.incidentID === selectedAlertID
          ? {
            ...inc,
            isUnreadMessage: false,
          } : inc))
    }
  }, []);

  const fetchIncidents = useCallback(async () => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      setError(null);
      const controller = new AbortController();
      const signal = controller.signal;
      const response = await incidentsApi.getAll(signal, baseLocation);
      if (!response?.data) return;
      setIncidents(response.data);
      setMapIncidents(processIncidents(response.data));
      setIncidentCount(response.data.length);
    } catch (error) {
      console.error("Failed to fetch incidents:", error);
      setError("Failed to fetch incidents. Please try again.");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseLocation]);

  const refreshIncidents = useCallback(async () => {
    setIncidents([]);
    setMapIncidents([]);
    await fetchIncidents();
  }, [fetchIncidents]);


  usePollingSubscription(
    `admin_incidents_${baseLocation || 'global'}`,
    async () => {
      if (!isAuthenticated) return null;
      return incidentsApi.getAll(undefined, baseLocation);
    },
    (response) => {
      if (response?.data) {
        const newIncidents = response.data;

        // Diffing logic for toasts
        const currentIds = new Set(incidents.map(i => i.incidentID));
        const newIds = new Set(newIncidents.map((i: any) => i.incidentID));

        newIncidents.forEach((inc: any) => {
          if (!currentIds.has(inc.incidentID)) {
            toast.warning(`New alert: Case #${inc.incidentID} reported`, {
              id: `new-incident-${inc.incidentID}`,
              duration: 5000,
            });
          }
        });

        // Detect resolved incidents
        incidents.forEach((inc: any) => {
          if (!newIds.has(inc.incidentID)) {
            toast.success(`Alert resolved: Case #${inc.incidentID}`, {
              id: `resolved-incident-${inc.incidentID}`,
              duration: 5000,
            });

            const savedAlert = localStorage.getItem("selected_alert_id");
            if (inc.incidentID == Number(savedAlert)) {
              setSelectedAlertId(null);
            }
          }
        });

        setIncidents(newIncidents);
        setMapIncidents(processIncidents(newIncidents));
        setIncidentCount(newIncidents.length);
        setSubscriptionStatus("connected");
      }
    },
    5000,
    [isAuthenticated, baseLocation]
  );

  const memoizedValue = useMemo(
    () => ({
      incidents,
      mapIncidents,
      isLoading,
      error,
      fetchIncidents,
      refreshIncidents,
      // setSubscriptionActive: setActiveSubscription,
      subscriptionStatus,
      incidentCount,
      getLocationAddressOfCurrentLatLng,
      markAsReadMessage
    }),
    [
      incidents,
      mapIncidents,
      isLoading,
      error,
      fetchIncidents,
      refreshIncidents,
      subscriptionStatus,
      incidentCount,
      getLocationAddressOfCurrentLatLng,
      markAsReadMessage
    ]
  );

  return (
    <IncidentsContext.Provider value={memoizedValue}>
      {children}
    </IncidentsContext.Provider>
  );
}

export function useIncidents() {
  const context = useContext(IncidentsContext);
  if (!context) {
    throw new Error("useIncidents must be used within an IncidentsProvider");
  }
  return context;
}
